import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import wretch from 'wretch';
import QueryStringAddon from "wretch/addons/queryString";
import moment from 'moment-timezone';
import { DevicesContext } from '../__state/Devices';
import { DeviceEntriesContext } from '../__state/DeviceEntries';
import { PaginationContext } from '../__state/Pagination';
import { ACCT_PLAN_LEVELS } from './constants';
import __isEqual from 'lodash/isEqual';
import __isEmpty from 'lodash/isEmpty';
import __findIndex from 'lodash/findIndex';
import __last from 'lodash/last';

export const useDeepEffect = (fn, deps) => {
	const isFirst = useRef(true);
	const prevDeps = useRef(deps);

	useEffect(() => {
		if (isFirst.current || !__isEqual(prevDeps.current, deps)) {
			fn();
		}
		isFirst.current = false;
		prevDeps.current = deps;
	}, [deps, fn]);
};

export const useDevices = (isLoggingOut, setIsLoading, detectValidToken) => {
	const { username, viewPermissions, token } = JSON.parse(sessionStorage.getItem('selfData') || '{}');
	const [devices, setDevices] = useState([]);
	const [totalDeviceCount, setTotalDeviceCount] = useState(0);
	const { cachedDevices, setCachedDevices } = useContext(DevicesContext);

  const fetchDevices = useCallback(async () => {
    try {
      await detectValidToken();
  
	  // eslint-disable-next-line no-unused-vars
      const { deviceswithFullInfo, devicesWithBasicInfo } = await wretch(
        `${process.env.REACT_APP_UTIL_API_URL}/user/units`
      )
        .addon(QueryStringAddon)
        .auth(`Bearer ${token}`)
        .query({ email: username })
        .get()
        .json();
  
      const filteredBasic = viewPermissions === 'ALL'
        ? devicesWithBasicInfo
        : devicesWithBasicInfo.filter(d => viewPermissions.includes(d.imei));
  
      const filteredFull = viewPermissions === 'ALL'
        ? deviceswithFullInfo
        : deviceswithFullInfo.filter(d => viewPermissions.includes(d.imei));
  
      const sortedDevices = filteredFull.sort((a, b) => a.imei.localeCompare(b.imei));
  
      setCachedDevices(filteredBasic);
      setTotalDeviceCount(sortedDevices.length);
  
      const payloadDevices = sortedDevices.map(device => ({
        id: device.id,
        type: device.type,
        imei: device.imei,
      }));
  
      const latestEntries = await fetchLatestEntries(token, payloadDevices);
		const finalDevices = sortedDevices.map(device => {
		const latest = latestEntries[device.id] || {};
		const rawTS = latest.latestEntryTS || 0;
		return {
			...device,
			...latest,
			latestEntryRaw: rawTS,
			latestEntryTS: rawTS 
			? moment.utc(Number(rawTS)).format("YYYY-MM-DD HH:mm:ss") 
			: null

		};
		});

		finalDevices.sort((a, b) => b.latestEntryRaw - a.latestEntryRaw);
      setDevices(finalDevices); 
    } catch (error) {
      console.error('Device fetch error:', error);
      alert('Could not retrieve your devices. Please refresh or contact support.');
    } finally {
      setIsLoading(false);
    }
  }, [cachedDevices, token, username, viewPermissions, detectValidToken]);
  
	useDeepEffect(() => {
		if (!isLoggingOut && devices.length === 0) {
			fetchDevices();
		}
	}, [isLoggingOut, devices]);

	return { devices, setDevices, totalDeviceCount };
};

// Standalone helper functions
async function fetchLatestEntries(token, devices) {
	return wretch(`${process.env.REACT_APP_UTIL_API_URL}/unit/entries/latest/batch`)
		.auth(`Bearer ${token}`)
		.post({ devices })
		.json();
}

export const useEntries = (
	device,
	setAreEntriesLoading,
	setEntryStartTimeIndex,
	setEntryEndTimeIndex,
	planLevel,
) => {
	const { token } = sessionStorage.getItem('selfData') ? JSON.parse(sessionStorage.getItem('selfData')) : {};
	const [deviceEntries, setDeviceEntries] = useState([]);
	const [totalEntriesCount, setTotalEntriesCount] = useState(0);
	const [totalEntriesTimestamps, setTotalEntriesTimestamps] = useState([]);
	const { cachedDeviceEntries, addDeviceEntries } = useContext(DeviceEntriesContext);
	const { setShowFirstEntriesPage } = useContext(PaginationContext);

	useDeepEffect(() => {
		const retrieveEntries = async () => {
			try {
				let entries = cachedDeviceEntries[device.id];

				// adjust how far back customers can view data based on their plan level
				let startTs = moment().subtract(24, 'hours').valueOf();
				if (planLevel === ACCT_PLAN_LEVELS.ADVANCED) {
					startTs = moment().subtract(6, 'months').valueOf();
				} else if (planLevel === ACCT_PLAN_LEVELS.PRO) {
					startTs = moment().subtract(1, 'year').valueOf();
				} else if (planLevel === ACCT_PLAN_LEVELS.UNRESTRICTED) {
					startTs = 0;
				}

				// used for pagination
				if (__isEmpty(entries)) {
					const tsRes = await wretch(
						`${process.env.REACT_APP_UTIL_API_URL}/unit/entries/timestamps`,
					)
					.addon(QueryStringAddon)
					.auth(`Bearer ${token}`)
					.query({
						deviceId: device.id,
						startTs,
					})
					.get()
					.json();

					let start = 0;
					let end = 0;
					let length = 0;
					if (tsRes.timestamp) {
						// use 101 instead of 100 as a hack to force a second page if selected page size is 100
						// this will force the app to load more data once the second page is clicked
						// this is why pages are fixed at 100 entries, not sure about the appending part yet

						// timestamps are descending
						// start = earliest timestamp of the page, accessed safely
						// TODO: replace hardcode with Entries per page selection
						start =
							tsRes.timestamp && tsRes.timestamp[101]
								? tsRes.timestamp[101].ts
								: __last(tsRes.timestamp).ts; 

						// end = latest timestamp, incremented by 1 since api call parameter is not inclusive, otherwise our most recent time gets cut off
						end = ++tsRes.timestamp[0].ts;

						length = tsRes.timestamp.length;
					} else {
						// we get here if there are no entries since we have account levels now
						// levels place a limit on time ranges (e.g. basic is 24 hours and there
						// may not have been an entry during the last 24 hours)
						start = startTs;
						end = Date.now();
					}

					entries = await wretch(`${process.env.REACT_APP_UTIL_API_URL}/unit/entries`)
						.auth(`Bearer ${token}`)
						.post({
							device: device,
							startTime: start,
							endTime: end,
						})
						.json();

					const startIndex = __findIndex(tsRes.timestamp, ['ts', start]);
					// for this device, add the entries from start to end time to the deviceEntries list
					addDeviceEntries(device.id, entries);
					setTotalEntriesCount(length);
					sessionStorage.setItem(`${device.id}_length`, length);
					setTotalEntriesTimestamps(tsRes.timestamp || {});
					setEntryStartTimeIndex && setEntryStartTimeIndex(startIndex > 0 ? startIndex : 0); // if setEntryStartTimeIndex exists, set the start index to max(startIndex, 0)
					setEntryEndTimeIndex(0);
				}

				setDeviceEntries(entries);
			} catch (error) {
				console.log(error);

				if (error.text && JSON.parse(error.text).type === 'token') {
					alert('Your session has expired. Please log in again.');
					return;
				}

				return;
			} finally {
				setAreEntriesLoading(false);
				setShowFirstEntriesPage(false);
			}
		};

		if (cachedDeviceEntries[device.id] && cachedDeviceEntries[device.id].length) {
			setDeviceEntries(cachedDeviceEntries[device.id]);
			setTotalEntriesCount(Number(sessionStorage.getItem(`${device.id}_length`)));
		} else if (!__isEmpty(device)) {
			setAreEntriesLoading(true);
			retrieveEntries();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [device, setAreEntriesLoading, { ...cachedDeviceEntries }]);
	
	return {
		deviceEntries,
		totalEntriesCount,
		totalEntriesTimestamps,
	};
};